import { useState } from 'react';
import { Star, X, Heart, ArrowRight, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../vendor/GlassCard';

const QUESTIONS = [
  { key: 'delivered_as_described', label: 'Was the service delivered as described?', sub: 'Did the final work match what you ordered?' },
  { key: 'communication',          label: "How was the vendor's communication?",     sub: 'Replies, clarity, and responsiveness.' },
  { key: 'buy_again',              label: 'Would you buy from this vendor again?',   sub: 'Likely to come back or refer a friend.' },
];

const TIP_PRESETS = [5, 10, 20, 50];

function StarRow({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          aria-label={`${n} star${n === 1 ? '' : 's'}`}
          className="transition-transform hover:scale-110 focus:outline-none"
          data-testid={`${testId}-star-${n}`}
        >
          <Star
            size={32}
            className={n <= value ? 'fill-yellow-400 text-yellow-400 drop-shadow-[0_0_8px_rgba(252,211,77,0.5)]' : 'text-zinc-600'}
            strokeWidth={1.5}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * RateAndTipModal — Fiverr-style 2-step rate + optional tip flow.
 *
 *  Step 1: 3 questions × 5 stars each (+ optional comment)
 *  Step 2: Pick a preset tip ($5 / $10 / $20 / $50 / custom) or skip
 *          100% of tip goes to vendor — fine print disclaimed.
 */
export default function RateAndTipModal({ open, onClose, orderId, vendorName, getAuthHeaders, onComplete }) {
  const [step, setStep] = useState('rate'); // 'rate' | 'tip' | 'done'
  const [ratings, setRatings] = useState({ delivered_as_described: 0, communication: 0, buy_again: 0 });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);
  const [customTip, setCustomTip] = useState('');

  if (!open) return null;

  const allRated = QUESTIONS.every((q) => ratings[q.key] >= 1);

  const submitRating = async () => {
    if (!allRated) {
      toast.error('Tap a star for each question first');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/custom-orders/${orderId}/rate`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...ratings, comment: comment.trim() || null }),
      });
      if (r.ok) {
        toast.success('Thanks for rating!');
        setStep('tip');
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Could not save rating');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const startTipCheckout = async (amount) => {
    if (!amount || amount < 1) {
      toast.error('Minimum tip is $1.00');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/custom-orders/${orderId}/tip-checkout`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount, origin_url: window.location.origin }),
      });
      if (r.ok) {
        const d = await r.json();
        if (d.checkout_url) {
          window.location.href = d.checkout_url;
          return;
        }
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.detail || 'Could not start tip checkout');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const finish = () => {
    setStep('done');
    onComplete && onComplete();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4 animate-fade-in"
      onClick={onClose}
      data-testid="rate-tip-modal"
    >
      <div
        className="w-full max-w-lg bg-[#13153A] border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-[0_24px_80px_-20px_rgba(139,92,246,0.4)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-gradient-to-r from-fuchsia-500/10 to-violet-500/10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-fuchsia-300/80 font-bold">
              {step === 'rate' ? 'Step 1 of 2' : 'Step 2 of 2'}
            </p>
            <h2 className="text-white font-bold font-syne text-lg" data-testid="rate-tip-title">
              {step === 'rate' ? 'Rate your order' : 'Show some love?'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-all"
            aria-label="Close"
            data-testid="rate-tip-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          {step === 'rate' && (
            <div className="space-y-5">
              <p className="text-sm text-zinc-300">
                Your review helps other buyers and rewards{' '}
                <span className="text-fuchsia-200 font-semibold">{vendorName || 'the vendor'}</span> for great work.
              </p>

              {QUESTIONS.map((q) => (
                <div key={q.key} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-white font-semibold text-sm">{q.label}</p>
                  <p className="text-xs text-zinc-400 mt-0.5 mb-3">{q.sub}</p>
                  <StarRow
                    value={ratings[q.key]}
                    onChange={(v) => setRatings((prev) => ({ ...prev, [q.key]: v }))}
                    testId={`rating-${q.key}`}
                  />
                </div>
              ))}

              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2 font-semibold">
                  Optional comment
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, 2000))}
                  rows={3}
                  placeholder="Anything you'd tell other buyers about working with this vendor?"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-zinc-500 focus:border-fuchsia-400/40 focus:outline-none resize-none"
                  data-testid="rating-comment"
                />
                <p className="text-[10px] text-zinc-500 mt-1 text-right">{comment.length}/2000</p>
              </div>

              <button
                disabled={!allRated || submitting}
                onClick={submitRating}
                className="w-full py-3.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-bold inline-flex items-center justify-center gap-2 hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-rating-btn"
              >
                {submitting ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
                Submit rating · Continue
                <ArrowRight size={16} />
              </button>
            </div>
          )}

          {step === 'tip' && (
            <div className="space-y-5" data-testid="tip-step">
              <div className="text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-pink-400 via-rose-400 to-orange-300 flex items-center justify-center shadow-[0_0_30px_rgba(244,114,182,0.45)] mb-3">
                  <Heart size={24} className="text-white fill-white" />
                </div>
                <p className="text-white font-bold text-base">
                  Want to leave {vendorName || 'the vendor'} a tip?
                </p>
                <p className="text-zinc-400 text-sm mt-1">Totally optional — but always appreciated.</p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {TIP_PRESETS.map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setTipAmount(amt) || setCustomTip('')}
                    className={`py-3 rounded-xl border text-base font-bold transition-all ${
                      tipAmount === amt && !customTip
                        ? 'border-fuchsia-400 bg-fuchsia-500/15 text-white shadow-[0_0_20px_rgba(217,70,239,0.3)]'
                        : 'border-white/10 bg-white/[0.04] text-zinc-200 hover:border-fuchsia-400/40'
                    }`}
                    data-testid={`tip-preset-${amt}`}
                  >
                    ${amt}
                  </button>
                ))}
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-zinc-400 mb-2 font-semibold">
                  Or a custom amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-bold">$</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    inputMode="decimal"
                    value={customTip}
                    onChange={(e) => {
                      setCustomTip(e.target.value);
                      setTipAmount(parseFloat(e.target.value) || 0);
                    }}
                    placeholder="0"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl pl-8 pr-4 py-3 text-white text-base font-semibold focus:border-fuchsia-400/40 focus:outline-none"
                    data-testid="tip-custom-input"
                  />
                </div>
              </div>

              {/* Fine print — vendor keeps 100% of tip */}
              <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/[0.06] p-3.5">
                <p className="text-[11px] text-emerald-200 leading-relaxed">
                  <span className="font-bold">100% of your tip goes directly to {vendorName || 'the vendor'}.</span>{' '}
                  EntrepreNatives takes <span className="font-bold">0%</span> of tips — every cent is theirs.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={finish}
                  disabled={submitting}
                  className="flex-1 py-3 rounded-xl border border-white/10 bg-white/[0.03] text-zinc-300 font-semibold hover:bg-white/[0.06] transition-all disabled:opacity-50"
                  data-testid="skip-tip-btn"
                >
                  Skip
                </button>
                <button
                  onClick={() => startTipCheckout(tipAmount)}
                  disabled={!tipAmount || tipAmount < 1 || submitting}
                  className="flex-1 py-3 rounded-xl bg-gradient-to-r from-pink-500 via-rose-500 to-orange-400 text-white font-bold inline-flex items-center justify-center gap-2 hover:shadow-[0_0_30px_rgba(244,114,182,0.45)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="send-tip-btn"
                >
                  {submitting ? <Loader2 className="animate-spin" size={16} /> : <Heart size={16} />}
                  Tip ${tipAmount || 0}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
